/**
 * Address line and postal code fields: Unicode letters, numbers, whitespace, and only
 * these punctuation characters: # / ' - ( ) "
 * Other characters are removed as the user types.
 *
 * FieldMax (GFF-aligned):
 * - Address line: 30
 * - Postal code: 10
 * - City: 20 (validated with city sanitize in personNameValidation)
 * - State / province: 30 (free text from address search; not USA-only)
 * - US state abbreviation (legacy callers): 2
 */

import { clampPersonNameToMaxLength } from "./personNameValidation";

export const ADDRESS_LINE_MAX_LENGTH = 30;
export const POSTAL_CODE_MAX_LENGTH = 10;
export const CITY_FIELD_MAX_LENGTH = 20;
export const STATE_FIELD_MAX_LENGTH = 30;
export const US_STATE_FIELD_MAX_LENGTH = 2;

/** updateConsumerProfile / legacy APIs accept only a 2-letter senderState. */
export function isTwoLetterStateCode(value?: string | null): boolean {
	return /^[A-Za-z]{2}$/.test(String(value ?? "").trim());
}

/** Returns uppercase 2-letter code, or empty string when not a valid abbreviation. */
export function toTwoLetterStateCode(value?: string | null): string {
	const trimmed = String(value ?? "").trim();
	return isTwoLetterStateCode(trimmed) ? trimmed.toUpperCase() : "";
}

/** Punctuation allowed in address/postal (same set as previous ADDRESS_POSTAL_ALLOWED class). */
const ADDRESS_SPECIAL_CODEPOINTS = new Set<number>([
	0x23, 0x2f, 0x27, 0x2d, 0x28, 0x29, 0x22, // # / ' - ( ) "
	0x2018, 0x2019, 0x201c, 0x201d, // ‘ ’ “ ”
]);

/**
 * Validates one Unicode code point (string from for…of) without applying quantifiers to the
 * full user value — avoids ReDoS findings on long strings (Semgrep regex_dos).
 * Letter/number checks use only single-grapheme patterns.
 */
function isAllowedAddressPostalCodePoint(ch: string): boolean {
	const cp = ch.codePointAt(0)!;
	if (ADDRESS_SPECIAL_CODEPOINTS.has(cp)) return true;
	// Match ECMA `\s` for a single code unit / one code point (bounded input).
	if (/^\s$/u.test(ch)) return true;
	return /^\p{L}$/u.test(ch) || /^\p{N}$/u.test(ch);
}

/** Collapses runs of the same allowed special character to a single character (e.g. ##### → #). */
const ADDRESS_SPECIAL_REPEAT_GLOBAL =
	/([#/'\-()"\u2018\u2019\u201C\u201D])\1+/gu;

function collapseRepeatingAddressSpecials(value: string): string {
	return value.replace(ADDRESS_SPECIAL_REPEAT_GLOBAL, "$1");
}

/** True if the value contains two or more identical address/postal punctuation marks in a row. */
export function hasRepeatingAddressSpecialCharacters(value: string): boolean {
	return /([#/'\-()"\u2018\u2019\u201C\u201D])\1+/u.test(value);
}

/** Strips disallowed characters, collapses repeated specials, then clamps to max length when provided. */
export function sanitizeAddressOrPostalInput(
	value: string,
	maxLen?: number
): string {
	let cleaned = value.replace(/[^\p{L}\p{N}\s#/'\-()"\u2018\u2019\u201C\u201D]/gu, "");
	cleaned = collapseRepeatingAddressSpecials(cleaned);
	return maxLen !== undefined
		? clampPersonNameToMaxLength(cleaned, maxLen)
		: cleaned;
}

export function sanitizeAddressLineInput(value: string): string {
	return sanitizeAddressOrPostalInput(value, ADDRESS_LINE_MAX_LENGTH);
}

/**
 * Postal / ZIP: keep letters + digits only (US ZIP and CA-style codes like L3L 0B5).
 * Spaces and other punctuation are stripped (e.g. "L3L 0B5" → "L3L0B5").
 */
export function sanitizePostalCodeInput(value: string): string {
	return value
		.replace(/[^A-Za-z0-9]/g, "")
		.toUpperCase()
		.slice(0, POSTAL_CODE_MAX_LENGTH);
}

/** True if empty/whitespace-only, or if allowed charset, no repeating specials, and length (when maxLen set). */
export function isAddressOrPostalContentValid(
	value: string,
	maxLen?: number
): boolean {
	const t = value.trim();
	if (!t) return true;
	if (maxLen !== undefined && value.length > maxLen) return false;
	if (hasRepeatingAddressSpecialCharacters(value)) return false;
	for (const ch of value) {
		if (!isAllowedAddressPostalCodePoint(ch)) return false;
	}
	return true;
}
